/* ProxyPicker — choose how to source the proxy an account logs in through.
 *
 * Two modes: auto-provision (claim the next pool proxy) or manual (type a proxy
 * string outside the pool). Used at first connect and on Change-proxy. The claim
 * is provisional until the login succeeds.
 */
import { useEffect, useState } from 'react';
import { api, type SourceProxyRequest } from '../../api/client';
import { isValidProxyUrl, PROXY_FORMAT_HINT } from '../../utils/proxy';
import { C, Btn } from './onboardingKit';

type Mode = 'same' | 'auto' | 'manual';

interface ProxyPickerProps {
  accountId: string;
  /** 'first' locks the proxy in via login-proxy; 'change' re-sources via change-proxy. */
  variant: 'first' | 'change';
  /** Offer a "keep the proxy this account already uses" option (relogin only). */
  allowKeepCurrent?: boolean;
  onSourced: () => void;
  onCancel?: () => void;
}

const cardBase: React.CSSProperties = {
  padding: 16, borderRadius: 14, border: `1px solid ${C.line}`, cursor: 'pointer',
  background: 'color-mix(in srgb, var(--om-ink) 3%, transparent)', transition: 'all .15s',
};

export function ProxyPicker({ accountId, variant, allowKeepCurrent, onSourced, onCancel }: ProxyPickerProps) {
  const [mode, setMode] = useState<Mode>(allowKeepCurrent ? 'same' : 'auto');
  const [manual, setManual] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const info = await api.getProxyPool(accountId);
        if (!active) return;
        setEnabled(info.enabled);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load proxies');
      }
    })();
    return () => { active = false; };
  }, [accountId]);

  const submit = async () => {
    // "Keep current proxy" changes nothing server-side — go straight to login
    // against the proxy the account already holds.
    if (mode === 'same') {
      onSourced();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: SourceProxyRequest = { mode };
      if (mode === 'manual') body.proxy_url = manual.trim();
      if (variant === 'change') await api.changeProxy(accountId, body);
      else await api.setLoginProxy(accountId, body);
      onSourced();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set proxy');
    } finally {
      setBusy(false);
    }
  };

  const manualEntered = manual.trim().length > 0;
  const manualValid = manualEntered && isValidProxyUrl(manual);

  const canSubmit =
    enabled &&
    !busy &&
    (mode === 'same' || mode === 'auto' || (mode === 'manual' && manualValid));

  const opt = (m: Mode, title: string, desc: string) => (
    <div
      onClick={() => setMode(m)}
      style={{
        ...cardBase,
        borderColor: mode === m ? C.purple : C.line,
        background: mode === m ? 'color-mix(in srgb, var(--om-accent) 12%, transparent)' : cardBase.background,
      }}
    >
      <div style={{ color: C.text, fontWeight: 700, fontSize: 14.5 }}>{title}</div>
      <div style={{ color: C.muted, fontSize: 13, marginTop: 4, lineHeight: 1.45 }}>{desc}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {!enabled && (
        <div style={{ color: '#ffb454', fontSize: 13.5 }}>
          Live login isn’t configured on this server yet.
        </div>
      )}
      {allowKeepCurrent && opt('same', 'Keep current proxy', 'Re-login through the proxy this account already uses. Recommended.')}
      {opt('auto', 'Auto-provision', 'Claim a new proxy from the pool.')}
      {opt('manual', 'Enter manually', 'Paste a proxy outside the pool (http://user:pass@host:port).')}
      {mode === 'manual' && (
        <input
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          placeholder="http://user:pass@host:port"
          style={{ padding: '11px 14px', borderRadius: 10, background: 'color-mix(in srgb, var(--om-ink) 5%, transparent)', color: C.text, border: `1px solid ${manualEntered && !manualValid ? '#ff6b6b' : C.line2}`, fontSize: 14, fontFamily: 'var(--font-mono)' }}
        />
      )}
      {mode === 'manual' && manualEntered && !manualValid && (
        <div style={{ color: '#ff6b6b', fontSize: 12.5 }}>
          Invalid proxy format — use {PROXY_FORMAT_HINT}
        </div>
      )}
      {error && <div style={{ color: '#ff6b6b', fontSize: 13 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
        <Btn onClick={submit} disabled={!canSubmit} icon={busy ? undefined : 'arrow'}>
          {busy
            ? 'Setting up…'
            : mode === 'same'
              ? 'Re-login'
              : variant === 'change'
                ? 'Change & re-login'
                : 'Start login'}
        </Btn>
        {onCancel && <Btn kind="ghost" onClick={onCancel} disabled={busy}>Cancel</Btn>}
      </div>
    </div>
  );
}
